import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/use-api'
import { scopeOf, useAdminApiBase } from './admin-base'
import type { TeamRepFunnel } from '@/features/team/use-team'

export type AdminOverview = {
  counts: {
    users: number
    leads: number
    campaigns: number
    posts: number
    messages: number
    connected_accounts: number
  }
  plans: Array<{ plan: string; n: number }>
  comp_count?: number
  /** Activated vs Paid split (shared user-funnel). Null/absent on failure or an older worker. */
  funnel?: AdminUserFunnel | null
  /** Team console only: per-rep invites / accepts / replies / calls, this week vs last. */
  rep_funnel?: TeamRepFunnel
  recent_activity_24h: Array<{ action_type: string; n: number }>
}

export type AdminUserFunnel = {
  total: number
  paid: number
  free_month: number
  comp: number
  unpaid: number
  activated: number
  activated_paid: number
  activated_free_month: number
  activated_comp: number
  activated_unpaid: number
}

export type AdminUserRow = {
  id: string
  email: string
  full_name: string | null
  plan: string
  plan_status: string
  /** 0/1 friends-and-family override (migration 0024). When 1, plan tier is
   * effectively 'agency' regardless of `plan`, and Stripe is bypassed. */
  is_comp?: number
  comp_granted_by?: string | null
  comp_granted_at?: string | null
  stripe_subscription_id?: string | null
  stripe_customer_id?: string | null
  created_at: string
  timezone: string | null
  leads_count: number
  campaigns_count: number
  posts_count: number
  sent_count: number
  received_count: number
  last_activity_at: string | null
  linkedin_name: string | null
  /** Staff console only. */
  unipile_account_id?: string | null
  /** Team console only: the rep's org role (manager | member | owner | admin). */
  role?: string
}

export type AdminLeadRow = {
  id: string
  user_id: string
  full_name: string | null
  first_name: string | null
  last_name: string | null
  linkedin_url: string
  company: string | null
  title: string | null
  status: string
  icp_score: number | null
  created_at: string
  owner_email: string
  owner_name: string | null
}

export type AdminCampaignRow = {
  id: string
  user_id: string
  name: string
  status: string
  daily_limit: number
  stats_sent: number
  stats_replied: number
  stats_meetings: number
  leads_count: number
  created_at: string
  updated_at: string
  owner_email: string
  owner_name: string | null
}

export type AdminPostRow = {
  id: string
  user_id: string
  content: string
  topic: string | null
  content_type: string
  status: string
  scheduled_for: string | null
  published_at: string | null
  linkedin_post_id: string | null
  metrics_likes: number
  metrics_comments: number
  created_at: string
  owner_email: string
  owner_name: string | null
}

export type AdminConversationRow = {
  user_id: string
  lead_id: string
  lead_name: string | null
  lead_linkedin_url: string
  lead_status: string
  lead_company: string | null
  owner_email: string
  owner_name: string | null
  last_message_at: string
  inbound_count: number
  outbound_count: number
  total_messages: number
}

export type AdminMessageRow = {
  id: string
  user_id: string
  lead_id: string
  campaign_id: string | null
  direction: 'inbound' | 'outbound'
  message_type: string
  content: string
  sent_at: string | null
  created_at: string
  status: string | null
  sentiment: string | null
}

const POLL_MS = 30_000

export function useAdminOverview() {
  const api = useApi()
  const base = useAdminApiBase()
  const scope = scopeOf(base)
  return useQuery({
    queryKey: [scope, 'overview'],
    queryFn: async () => {
      const res = await api<AdminOverview>(`${base}/overview`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: POLL_MS,
  })
}

export function useAdminUsers() {
  const api = useApi()
  const base = useAdminApiBase()
  const scope = scopeOf(base)
  return useQuery({
    queryKey: [scope, 'users'],
    queryFn: async () => {
      const res = await api<{ users: AdminUserRow[] }>(`${base}/users`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.users
    },
    refetchInterval: POLL_MS,
  })
}

export function useAdminLeads(filterUserId: string | null) {
  const api = useApi()
  const base = useAdminApiBase()
  const scope = scopeOf(base)
  return useQuery({
    queryKey: [scope, 'leads', filterUserId],
    queryFn: async () => {
      const path = filterUserId
        ? `${base}/leads?user_id=${encodeURIComponent(filterUserId)}`
        : `${base}/leads`
      const res = await api<{ leads: AdminLeadRow[] }>(path)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.leads
    },
    refetchInterval: POLL_MS,
  })
}

export function useAdminCampaigns(filterUserId: string | null) {
  const api = useApi()
  const base = useAdminApiBase()
  const scope = scopeOf(base)
  return useQuery({
    queryKey: [scope, 'campaigns', filterUserId],
    queryFn: async () => {
      const path = filterUserId
        ? `${base}/campaigns?user_id=${encodeURIComponent(filterUserId)}`
        : `${base}/campaigns`
      const res = await api<{ campaigns: AdminCampaignRow[] }>(path)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.campaigns
    },
    refetchInterval: POLL_MS,
  })
}

export function useAdminContent(filterUserId: string | null) {
  const api = useApi()
  const base = useAdminApiBase()
  const scope = scopeOf(base)
  return useQuery({
    queryKey: [scope, 'content', filterUserId],
    queryFn: async () => {
      const path = filterUserId
        ? `${base}/content?user_id=${encodeURIComponent(filterUserId)}`
        : `${base}/content`
      const res = await api<{ posts: AdminPostRow[] }>(path)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.posts
    },
    refetchInterval: POLL_MS,
  })
}

export function useAdminInbox(filterUserId: string | null) {
  const api = useApi()
  const base = useAdminApiBase()
  const scope = scopeOf(base)
  return useQuery({
    queryKey: [scope, 'inbox', filterUserId],
    queryFn: async () => {
      const path = filterUserId
        ? `${base}/inbox?user_id=${encodeURIComponent(filterUserId)}`
        : `${base}/inbox`
      const res = await api<{ conversations: AdminConversationRow[] }>(path)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.conversations
    },
    refetchInterval: POLL_MS,
  })
}

// ---------------------------------------------------------------------------
// API Spend — backs the /super-admin/spend page.
//
// Reads three D1 ledgers via the leads worker's /admin/spend route:
//   - ai_spend_ledger      — @dealcloser/ai-gateway writes one row per
//                            Anthropic / OpenAI / Perplexity call
//   - unipile_spend_ledger — @dealcloser/unipile-gateway writes one row per
//                            Unipile call (cron + send paths + search)
//   - apollo_spend_ledger  — clients/apollo.ts records bulk_match credits
//
// Pre-launch caps total $15/day across the four USD-billed services:
//   Anthropic $5 + OpenAI $2 + Perplexity $4 + Unipile $4
// Apollo bills monthly in credits (default 2500/mo = Basic plan ceiling).
//
// Polling every 30s so the page acts as a live monitor during the F&F beta.
// Each poll is bounded to ~10 small D1 aggregate queries on the worker side
// — well under the ledger overhead it's monitoring.
// ---------------------------------------------------------------------------

export type AdminSpendBreakdownRow = {
  source: string
  usd: number
  calls: number
}

export type AdminSpendUserRow = {
  user_id: string | null
  usd: number
  calls: number
}

export type AdminServiceCard = {
  service: 'anthropic' | 'openai' | 'perplexity'
  /** Always 'usd' — every LLM provider is USD-capped. Unipile was removed
   *  from this module 2026-06-10 (subscription-billed, separate concern). */
  throttle_kind: 'usd'
  today_usd: number
  cap_usd: number
  headroom_usd: number
  pct_used: number
  /** Optional billing-model explainer shown under the card title. */
  billing_note?: string
  /** Anthropic only (2026-09-26): cap_usd = sum of every user's daily cap. */
  cap_basis?: 'sum_of_user_caps'
  /** Anthropic only: busiest user's spend as % of their own cap. */
  max_user_pct_used?: number
  max_user_email?: string | null
  users_at_cap?: number
  by_source: AdminSpendBreakdownRow[]
  by_user: AdminSpendUserRow[]
}

export type AdminPerUserDailyRow = {
  user_id: string | null
  email: string
  plan: string
  is_comp: number
  anthropic_usd: number
  openai_usd: number
  perplexity_usd: number
  /** Ollama chat and web search. Optional so an older worker deploy does not break the page. */
  ollama_usd?: number
  /** Ollama web searches this day and the user's daily web search cap (counted, not priced). */
  web_searches?: number
  web_search_cap?: number
  total_usd: number
  ai_calls: number
  daily_cap_usd: number
  pct_used: number
}

export type AdminOrgSummary = {
  total_daily_spend_usd: number
  total_daily_limit_usd: number
  total_daily_headroom_usd: number
  pct_used: number
  user_count: number
  default_per_user_cap_usd: number
}

export type AdminApolloLiveHealth = {
  credits_used: number | null
  credits_limit: number | null
  credits_remaining: number | null
  plan_name: string | null
  fetched_at: string
}

export type AdminSpendApolloCard = {
  credits_today: number
  credits_mtd: number
  cap_credits_monthly: number
  headroom_mtd: number
  pct_used: number
  by_user: Array<{ user_id: string | null; credits: number }>
  /** Live Apollo account state from GET /v1/auth/health. Null when the
   *  endpoint failed or APOLLO_API_KEY is unset; UI then falls back to
   *  the local ledger-based figures (credits_mtd / headroom_mtd). */
  live?: AdminApolloLiveHealth | null
  /** Sum of per-user Apollo monthly quotas (user_settings.apollo_monthly_quota_credits).
   *  Shown next to the cap so staff can spot over-allocation. */
  assigned_quota_credits?: number
  /** True when assigned_quota_credits > cap_credits_monthly — drives the
   *  red over-allocation warning banner on /super-admin/spend. */
  assigned_quota_over_cap?: boolean
  /** Sum of every user's CURRENT unspent credit balance. Each of these,
   *  when spent on enrichment, consumes one Apollo credit — so they are
   *  committed even though not yet used. */
  assigned_unspent_credits?: number
  /** credits_mtd + assigned_unspent_credits. */
  committed_credits?: number
  /** cap - used MTD - assigned unspent. NEGATIVE = over-committed. */
  uncommitted_headroom?: number
}

export type AdminFixedCost = {
  id: string
  name: string
  category: 'infrastructure' | 'api' | 'tooling' | 'usage-based'
  usdPerMonth: number
  /** false = estimated from public pricing, not confirmed against an invoice. */
  verified: boolean
  note: string
}

export type AdminCosts = {
  items: AdminFixedCost[]
  fixed_monthly_usd: number
  ai_mtd_usd: number
  /** MTD usage scaled to a full month so it can be added to subscriptions. */
  ai_projected_monthly_usd: number
  /** fixed + projected usage = what a full month actually costs. */
  total_run_rate_usd: number
  /** True while any line is still an estimate rather than a real invoice. */
  estimated: boolean
  unverified_count: number
  paying_users: number
  mrr_usd: number
  /** null when there are no paying customers to divide by. */
  cost_per_paying_user_usd: number | null
  gross_margin_usd: number | null
}

export type AdminSpendData = {
  day: string
  /** Sum of today_usd across all four USD-billed services. */
  total_usd: number
  /** Sum of the four per-service caps — what the org is allowed to spend today. */
  total_cap_usd: number
  total_headroom_usd: number
  services: AdminServiceCard[]
  apollo: AdminSpendApolloCard
  /** Per-user breakdown for the target day, sorted by total_usd desc.
   *  Includes ALL services (Anthropic, OpenAI, Perplexity, Unipile). */
  by_user_daily: AdminPerUserDailyRow[]
  /** Org-wide grand totals + per-user-cap aggregation. */
  org_summary: AdminOrgSummary
  /** Monthly cost of the tools and APIs the product runs on, plus what that
   *  works out to per paying customer. Optional so an older worker deploy
   *  does not break the page. */
  costs?: AdminCosts
  // Legacy single-service shape preserved for back-compat. New code should
  // read `services` + `apollo`; these fields refer to Claude only and will
  // be removed once the dashboard fully cuts over.
  today_usd: number
  cap_usd: number
  headroom_usd: number
  by_source: AdminSpendBreakdownRow[]
  by_user: AdminSpendUserRow[]
}

export function useAdminSpend(day?: string) {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'spend', day ?? 'today'],
    queryFn: async () => {
      const path = day
        ? `/api/admin/spend?day=${encodeURIComponent(day)}`
        : '/api/admin/spend'
      const res = await api<AdminSpendData>(path)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: POLL_MS,
  })
}

export function useAdminInboxMessages(leadId: string | null) {
  const api = useApi()
  const base = useAdminApiBase()
  const scope = scopeOf(base)
  return useQuery({
    queryKey: [scope, 'inbox', 'messages', leadId],
    queryFn: async () => {
      if (!leadId) return [] as AdminMessageRow[]
      const res = await api<{ messages: AdminMessageRow[] }>(
        `${base}/inbox/${encodeURIComponent(leadId)}/messages`,
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.messages
    },
    enabled: Boolean(leadId),
  })
}

// ---------------------------------------------------------------------------
// Comp toggle (migration 0024 — friends-and-family override).
// POST /admin/users/:id/comp { enabled }
//   enabled=true: flips is_comp=1 and tops credits up to the stored plan's
//                 monthly allocation (Basic 100 / Pro 200), never beyond.
//   enabled=false: clears the flag; users.plan/plan_status are left
//                  untouched (so the user reverts to whatever they had).
//
// Stripe-subscription cancellation is intentionally NOT done here — the
// end user (or DCA staff via stripe.com Dashboard) cancels themselves.
// The comp guard in the webhooks worker short-circuits the cancellation
// event so Unipile stays connected and plan_status doesn't flip.
//
// The user-facing dashboard does NOT change for comp users — they appear
// as a normal Agency seat (no "COMP" badge, no F&F card).
// ---------------------------------------------------------------------------

export type CompToggleResponse = {
  user_id: string
  is_comp: 0 | 1
  granted_by?: string
  previous_plan?: string
  previous_status?: string
  previous_subscription_id?: string | null
  reverted_to_plan?: string
  reverted_to_status?: string
  credits_allocated?: number
}

export function useToggleComp() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { userId: string; enabled: boolean }) => {
      const res = await api<CompToggleResponse>(
        `/api/admin/users/${encodeURIComponent(vars.userId)}/comp`,
        { method: 'POST', body: { enabled: vars.enabled } },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      qc.invalidateQueries({ queryKey: ['admin', 'overview'] })
    },
  })
}

export type AdminCompAuditRow = {
  id: string
  action_type: 'comp_granted' | 'comp_revoked'
  details: string
  created_at: string
}

export function useAdminCompAudit(userId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'comp-audit', userId],
    queryFn: async () => {
      if (!userId) return [] as AdminCompAuditRow[]
      const res = await api<{ audit: AdminCompAuditRow[] }>(
        `/api/admin/users/${encodeURIComponent(userId)}/comp/audit`,
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.audit
    },
    enabled: Boolean(userId),
  })
}

// ---------------------------------------------------------------------------
// Free-month promo codes (webhooks worker, /api/billing/admin/*).
//
// Staff generate single-use Stripe promotion codes on demand. A redeeming
// customer gets their first month free; during that month the AI router
// serves them the Basic-tier model for message generation even on Pro
// (user_settings.basic_model_until). Everything else about their plan is
// unchanged.
// ---------------------------------------------------------------------------

export type FreeMonthCodeRow = {
  id: string
  code: string
  active: boolean
  times_redeemed: number
  max_redemptions: number | null
  created_at: string
}

export function useFreeMonthCodes() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'free-month-codes'],
    queryFn: async () => {
      const res = await api<{ codes: FreeMonthCodeRow[] }>('/api/billing/admin/free-month-codes')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.codes
    },
  })
}

export function useGenerateFreeMonthCode() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { code?: string }) => {
      const res = await api<{ code: string; promotion_code_id: string }>(
        '/api/billing/admin/free-month-code',
        { method: 'POST', body: vars.code ? { code: vars.code } : {} },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'free-month-codes'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Orchestrator observability (in-backend engine traces, migration 0026)
// ---------------------------------------------------------------------------

export type OrchestratorRun = {
  id: string
  started_at: string
  finished_at: string | null
  mode: string
  trigger: string
  considered: number
  emitted: number
  sent: number
  blocked: number
  deferred: number
  failed: number
  duration_ms: number | null
  error: string | null
}

export type OrchestratorRunsResponse = {
  runs: OrchestratorRun[]
  last_hour: {
    runs: number
    sent: number
    blocked: number
    deferred: number
    failed: number
  } | null
}

export type OrchestratorEvent = {
  id: string
  ts: string
  user_id: string | null
  campaign_id: string | null
  lead_id: string | null
  step_type: string | null
  phase: string
  outcome: string
  detail: string | null
  duration_ms: number | null
  lead_name: string | null
  campaign_name: string | null
}

export function useOrchestratorRuns() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'orchestrator', 'runs'],
    queryFn: async () => {
      const res = await api<OrchestratorRunsResponse>('/api/admin/orchestrator/runs?limit=60')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: 30_000,
  })
}

export function useOrchestratorRunEvents(runId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'orchestrator', 'events', runId],
    queryFn: async () => {
      const res = await api<{ events: OrchestratorEvent[] }>(
        `/api/admin/orchestrator/runs/${runId}/events`,
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.events
    },
    enabled: runId !== null,
  })
}

export type LifecycleState = {
  user_id: string
  stage: string
  entered_at: string
  updated_at: string
  email: string
  full_name: string | null
  plan: string
  plan_status: string
  signed_up_at: string
  utm_source: string | null
  utm_campaign: string | null
  referrer: string | null
}

export type LifecycleEmail = {
  user_id: string
  email_key: string
  sent_at: string
  email: string
}

export type CapturedLead = {
  id: string
  email: string
  source: string
  projected_revenue: number | null
  avg_deal_value: number | null
  calls_per_month: number | null
  utm_source: string | null
  emailed_at: string | null
  created_at: string
  converted: number
}

export type LifecycleResponse = {
  states: LifecycleState[]
  emails: LifecycleEmail[]
  stage_counts: { stage: string; n: number }[]
  source_counts: { source: string; n: number }[]
  captured_leads: CapturedLead[]
}

export function useLifecycle() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'lifecycle'],
    queryFn: async () => {
      const res = await api<LifecycleResponse>('/api/admin/lifecycle')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: 60_000,
  })
}

// ---------------------------------------------------------------------------
// SEO monitor — backs the /super-admin/seo tab. Rows are written by the daily
// crawl (seo/monitor.py) via POST /campaigns/internal/seo/run, so this reads
// our own D1 rather than reaching out to GitHub.
// ---------------------------------------------------------------------------

export type SeoIssue = {
  severity: 'high' | 'medium' | 'low'
  page: string
  issue: string
}

export type SeoPageInfo = {
  title_len: number
  desc_len: number
  h1_count: number
  words: number
  jsonld_blocks: number
  jsonld_invalid: number
  images: number
  images_no_alt: number
}

export type SeoTarget = {
  keyword: string
  path: string
  intent: string
  status: string
  why?: string
}

export type SeoRunSummary = {
  ran_at: string
  pages_crawled: number
  high: number
  medium: number
  low: number
  new_count: number
  fixed_count: number
}

export type SeoLatestRun = SeoRunSummary & {
  id: string
  issues: SeoIssue[]
  pages: Record<string, SeoPageInfo>
  targets_todo: SeoTarget[]
  report_md: string | null
}

export type SeoResponse = {
  latest: SeoLatestRun | null
  /** Oldest first, so a chart reads left to right. */
  history: SeoRunSummary[]
}

export function useAdminSeo() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'seo'],
    queryFn: async () => {
      const res = await api<SeoResponse>('/api/admin/seo')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    // The crawl runs once a day, so polling hard would be pointless.
    refetchInterval: 5 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// Tracked short links + payout (leads worker, /api/admin/links, migration
// 0037). Eli and TJ share https://dealcloserautopilot.com/r/<code> in DMs;
// the redirect stamps utm_campaign=<code>, so signups attribute back via
// user_attribution. Payout = flat + per link-attributed signup + per booked
// call platform-wide, over the selected date range.
// ---------------------------------------------------------------------------

export type AdminLinkRow = {
  code: string
  label: string
  destination: string
  owner_user_id: string | null
  owner_email: string | null
  share_url: string
  created_at: string
  clicks: number
  unique_visitors: number
  signups: number
  paid: number
  booked_calls: number
}

export type AdminLinksRange = { from: string; to: string }

export type AdminLinksResponse = {
  range: AdminLinksRange
  links: AdminLinkRow[]
  platform: {
    signups: number
    paid: number
    booked_calls: number
    booked_calls_by_user: Array<{ user_id: string; email: string | null; count: number }>
  }
  payout: {
    flat_usd: number
    per_signup_usd: number
    per_booked_call_usd: number
    link_signups: number
    platform_booked_calls: number
    total_usd: number
  }
}

export function useAdminLinks(range: AdminLinksRange) {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'links', range.from, range.to],
    queryFn: async () => {
      const qs = new URLSearchParams({ from: range.from, to: range.to })
      const res = await api<AdminLinksResponse>(`/api/admin/links?${qs.toString()}`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: 60_000,
  })
}

// ---------------------------------------------------------------------------
// Message Audit (leads worker, /api/admin/message-audit, 2026-08-31).
//
// Full-database message health scan: leads waiting on an AI reply (the
// 2026-08-30 "9 unanswered replies" incident class), flagged leads,
// review-queue backlogs, send failures, budget saturation, and broken
// campaign CTAs — plus heuristic per-user suggestions. The fix mutation is
// the proven recovery for stuck replies: clear needs_attention_* and retry
// state so the reconcile re-drive (every 5 min) answers automatically.
// ---------------------------------------------------------------------------

export type MessageAuditSeverity = 'critical' | 'warn' | 'info'

export type MessageAuditUnansweredItem = {
  lead_id: string
  lead_name: string | null
  company: string | null
  user_id: string
  owner_email: string
  lead_status: string
  hours_waiting: number
  needs_attention_reason: string | null
  needs_attention_at: string | null
  send_retry_after: string | null
  reply_attempts: number
  last_failure_code: string | null
  classification: string | null
  /** flagged = stuck until fixed; parked = self-heals; overdue = re-drive lagging. */
  state: 'flagged' | 'parked' | 'overdue'
}

export type MessageAuditIssue = {
  type: string
  severity: MessageAuditSeverity
  title: string
  description: string
  fixable: boolean
  total: number
  /** Shape varies per issue type; the panel renders what it knows. */
  items: Array<Record<string, unknown>>
}

export type MessageAuditUserStats = {
  user_id: string
  email: string
  plan: string
  sent_30d: number
  received_30d: number
  answered_30d: number
  median_response_hours: number | null
  positive_share: number | null
}

export type MessageAuditCampaignStats = {
  campaign_id: string
  name: string
  user_id: string
  owner_email: string
  status: string
  stats_sent: number
  stats_replied: number
  sent_7d: number
  reply_rate: number | null
}

export type MessageAuditSuggestion = {
  user_email: string
  suggestion: string
  evidence: string
}

export type MessageAuditData = {
  generated_at: string
  issues: MessageAuditIssue[]
  stats: {
    users: MessageAuditUserStats[]
    campaigns: MessageAuditCampaignStats[]
    budget_hit_days: Array<{ user_id: string; email: string; days_hit: number }>
  }
  suggestions: MessageAuditSuggestion[]
  /** False → the worker can't reach the AI worker; hide the Ask-AI button. */
  ai_available: boolean
}

export function useMessageAudit() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'message-audit'],
    queryFn: async () => {
      const res = await api<MessageAuditData>('/api/admin/message-audit')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    // The scan is ~14 D1 aggregates — cheap enough to keep live, heavy
    // enough not to hammer at the 30s cadence the lighter tabs use.
    refetchInterval: 60_000,
  })
}

export type MessageAuditFixResult = {
  fixed: number
  skipped: Array<{ id: string; reason: string }>
}

export function useMessageAuditFix() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { lead_ids: string[] }) => {
      const res = await api<MessageAuditFixResult>('/api/admin/message-audit/fix', {
        method: 'POST',
        body: { type: 'requeue_replies', lead_ids: vars.lead_ids },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    onSuccess: () => {
      // Re-scan so the fixed leads leave the critical list immediately.
      qc.invalidateQueries({ queryKey: ['admin', 'message-audit'] })
    },
  })
}

export type MessageAuditAiSuggestResult = {
  user_id: string
  conversations_analyzed: number
  suggestions_text: string
}

export function useMessageAuditAiSuggest() {
  const api = useApi()
  return useMutation({
    mutationFn: async (vars: { user_id: string }) => {
      const res = await api<MessageAuditAiSuggestResult>('/api/admin/message-audit/suggest-ai', {
        method: 'POST',
        body: { user_id: vars.user_id },
      })
      // 501 = ai_analysis_unavailable (no AI binding on the worker yet).
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
  })
}

// ---------------------------------------------------------------------------
// Message Insights (leads worker, /api/admin/message-insights, 2026-10-03).
//
// Review surface for the DAILY message audit job (campaigns worker, 13:13
// UTC): issues it found, which ones it auto-fixed (requeue only), and the
// AI's proposed SKILL.md changes. Accept only marks a suggestion accepted;
// nothing ever edits SKILL.md automatically (standing owner decision).
// ---------------------------------------------------------------------------

export type InsightIssueStatus = 'open' | 'auto_fixed' | 'resolved' | 'dismissed'
export type InsightSuggestionStatus = 'new' | 'accepted' | 'dismissed'

export type InsightIssue = {
  id: string
  type: string
  severity: MessageAuditSeverity
  user_id: string | null
  lead_id: string | null
  campaign_id: string | null
  title: string
  detail: Record<string, unknown> | null
  message_ids: string[]
  status: InsightIssueStatus
  fix_action: string | null
  fixed_at: string | null
  resolved_at: string | null
  first_seen_at: string
  last_seen_at: string
  occurrences: number
  notes: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  owner_email: string | null
}

export type InsightSuggestion = {
  id: string
  run_id: string | null
  severity: 'high' | 'medium' | 'low'
  category: string | null
  title: string
  problem: string
  proposed_change: string
  skill_section: string | null
  skill_patch: string
  /** ok = removed/context lines match the current SKILL.md; mismatch = they don't; no_anchor = additions only. */
  patch_check: 'ok' | 'mismatch' | 'no_anchor' | null
  evidence_message_ids: string[]
  evidence_lead_ids: string[]
  model: string | null
  status: InsightSuggestionStatus
  notes: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  created_at: string
}

export type InsightRun = {
  id: string
  started_at: string
  finished_at: string | null
  trigger: string
  issues_found: number
  issues_new: number
  auto_fixed: number
  issues_resolved: number
  conversations_sampled: number
  suggestions_created: number
  ai_status: string | null
  ai_model: string | null
  ai_cost_usd: number
  error: string | null
}

export type MessageInsightsData = {
  summary: {
    new_suggestions: number
    accepted_suggestions: number
    open_issues: number
    open_critical: number
    auto_fixed_7d: number
    ai_cost_today_usd: number
  } | null
  runs: InsightRun[]
  issues: InsightIssue[]
  suggestions: InsightSuggestion[]
}

export function useMessageInsights(opts: { allIssues: boolean; allSuggestions: boolean }) {
  const api = useApi()
  const qs = new URLSearchParams()
  if (opts.allIssues) qs.set('issues', 'all')
  if (opts.allSuggestions) qs.set('suggestions', 'all')
  const q = qs.toString()
  return useQuery({
    queryKey: ['admin', 'message-insights', opts],
    queryFn: async () => {
      const res = await api<MessageInsightsData>(`/api/admin/message-insights${q ? `?${q}` : ''}`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: 120_000,
  })
}

/** Nav badge: suggestions waiting for review. */
export function useMessageInsightsBadge() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'message-insights', 'badge'],
    queryFn: async () => {
      const res = await api<{ new_suggestions: number; open_critical: number }>('/api/admin/message-insights/badge')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: 300_000,
  })
}

export function useReviewInsight() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: {
      kind: 'suggestions' | 'issues'
      id: string
      status?: string
      notes?: string | null
    }) => {
      const body: Record<string, unknown> = {}
      if (vars.status !== undefined) body.status = vars.status
      if (vars.notes !== undefined) body.notes = vars.notes
      const res = await api<Record<string, unknown>>(
        `/api/admin/message-insights/${vars.kind}/${encodeURIComponent(vars.id)}`,
        { method: 'PATCH', body },
      )
      if (!res.success) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'message-insights'] })
    },
  })
}

export function useCreateAdminLink() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: {
      code: string
      label: string
      destination?: string
      owner_user_id?: string | null
    }) => {
      const res = await api<{ link: AdminLinkRow }>('/api/admin/links', {
        method: 'POST',
        body: vars,
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.link
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'links'] })
    },
  })
}

export function useArchiveAdminLink() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (code: string) => {
      const res = await api<{ code: string; archived: boolean }>(
        `/api/admin/links/${encodeURIComponent(code)}`,
        { method: 'DELETE' },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'links'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Custom plan inquiries (meeting 2026-09-28, migration 0040) — backs the
// /super-admin/custom-plans tab. Rows come from the marketing-site intake
// form; the checkout link is made by the webhooks worker.
// ---------------------------------------------------------------------------

export type CustomPlanStatus = 'new' | 'contacted' | 'call_booked' | 'quoted' | 'won' | 'lost'

export type CustomPlanInquiry = {
  id: string
  name: string
  email: string
  company: string
  role: string | null
  website: string | null
  team_size: string | null
  meetings_goal: string | null
  budget: string | null
  timeline: string | null
  needs: string | null
  details: string | null
  answers_json: string | null
  utm_source: string | null
  referrer: string | null
  status: CustomPlanStatus
  quote_usd: number | null
  staff_notes: string | null
  staff_notified_at: string | null
  confirmation_sent_at: string | null
  created_at: string
  updated_at: string
  user_id: string | null
  user_plan: string | null
  /** Enterprise onboarding after payment (migration 0052). */
  paid_at?: string | null
  onboarding_sent_at?: string | null
  onboarding_email?: string | null
  org_id?: string | null
}

export function useCustomPlanInquiries() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'custom-plans'],
    queryFn: async () => {
      const res = await api<{ inquiries: CustomPlanInquiry[] }>('/api/admin/custom-plans')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.inquiries
    },
    refetchInterval: 60_000,
  })
}

export function useUpdateCustomPlanInquiry() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: {
      id: string
      status?: CustomPlanStatus
      quote_usd?: number | null
      staff_notes?: string
      budget?: string
    }) => {
      const { id, ...body } = vars
      const res = await api<{ updated: boolean }>(`/api/admin/custom-plans/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body,
      })
      if (!res.success) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'custom-plans'] })
    },
  })
}

export function useCustomPlanCheckout() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { email: string; monthly_usd: number; inquiry_id?: string }) => {
      const res = await api<{ url: string; user_id: string; amount_cents: number }>(
        '/api/billing/admin/custom-plan-checkout',
        { method: 'POST', body: vars },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'custom-plans'] })
    },
  })
}

/** Staff: resend the client onboarding email for a paid inquiry (webhooks worker). */
export function useResendOnboardingEmail() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api<{ sent_to: string; onboarding_sent_at: string }>(
        `/api/billing/admin/custom-plans/${encodeURIComponent(id)}/resend-onboarding`,
        { method: 'POST' },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'custom-plans'] })
    },
  })
}

// ---------------------------------------------------------------------------
// MCP ("Use DCA in Claude") — backs the /super-admin/mcp tab (2026-10-03).
// Leads worker routes/admin-mcp.ts. mcp_activity is written by the MCP
// worker (migration 0050); until it exists the activity call returns
// table_missing: true with empty lists.
// ---------------------------------------------------------------------------

export type AdminMcpConnection = {
  id: string
  prefix: string
  label: string
  created_at: string | null
  expires_at: string | null
  last_used_at: string | null
  write: boolean
  skip_qg?: boolean
  expired: boolean
  user_id: string
  email: string | null
}

export type AdminMcpActivityRow = {
  id: string
  user_id: string
  email: string | null
  token_prefix: string | null
  tool: string
  ok: number
  error: string | null
  lead_id: string | null
  duration_ms: number | null
  created_at: string
}

export type AdminMcpUserCounts = {
  user_id: string
  email: string | null
  calls_24h: number
  errors_24h: number
  calls_7d: number
  errors_7d: number
  error_rate_7d: number
  last_call_at: string | null
}

export type AdminMcpActivity = {
  table_missing: boolean
  query_failed?: boolean
  rows: AdminMcpActivityRow[]
  per_user: AdminMcpUserCounts[]
  per_tool: Array<{ tool: string; calls: number; errors: number }>
  totals: { calls_24h: number; errors_24h: number; error_rate_24h: number; calls_7d: number; errors_7d: number; error_rate_7d: number }
}

export type AdminMcpActivityFilters = { user_id?: string; tool?: string; ok?: '' | '0' | '1'; hours?: number }

export function useAdminMcpConnections() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'mcp', 'connections'],
    queryFn: async () => {
      const res = await api<{ connections: AdminMcpConnection[]; writes_enabled: boolean }>('/api/admin/mcp/connections')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: 60_000,
  })
}

export function useAdminMcpActivity(filters: AdminMcpActivityFilters) {
  const api = useApi()
  const qs = new URLSearchParams()
  if (filters.user_id) qs.set('user_id', filters.user_id)
  if (filters.tool) qs.set('tool', filters.tool)
  if (filters.ok) qs.set('ok', filters.ok)
  if (filters.hours) qs.set('hours', String(filters.hours))
  const query = qs.toString()
  return useQuery({
    queryKey: ['admin', 'mcp', 'activity', query],
    queryFn: async () => {
      const res = await api<AdminMcpActivity>(`/api/admin/mcp/activity?${query}`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    refetchInterval: 60_000,
  })
}

export function useSetMcpWrites() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await api<{ writes_enabled: boolean }>('/api/admin/mcp/writes', { method: 'POST', body: { enabled } })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'mcp'] }),
  })
}

export function useAdminRevokeMcp() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api<{ revoked: boolean }>(`/api/admin/mcp/connections/${id}`, { method: 'DELETE' })
      if (!res.success) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'mcp'] }),
  })
}
